import React from "react";
import PropTypes from "prop-types";
import {
	PrimaryCard,
	PrimaryCardVariants,
	InfoCard,
	InfoCardVariants,
	CardBackgroundType,
	CardTheme,
	HotelCard,
} from "@sunwing/wvi-components";
import { usePageContext } from "../../context/PageContext";
import { RCL as useTranslation } from "../../components/RCL";
import { RichTextConnector } from "../RichTextConnector";
import { cloudinaryGenURL } from "../../utils/cloudinaryHelpers";

const CARD_VARIANT_MAP = {
	"Info Default": { component: InfoCard, variant: InfoCardVariants.default, showImage: false },
	"Info Horizontal": { component: InfoCard, variant: InfoCardVariants.horizontal, showImage: true },
	"Info Horizontal No Icon": {
		component: InfoCard,
		variant: InfoCardVariants.horizontalNoIcon,
		showImage: false,
	},
	"Hotel Default": {
		component: HotelCard,
		showImage: true,
	},
	"Primary Even Split": {
		component: PrimaryCard,
		variant: PrimaryCardVariants.evenSplit,
		showImage: true,
	},
	"Primary Logo Offset": {
		component: PrimaryCard,
		variant: PrimaryCardVariants.logoOffset,
		showImage: true,
	},
	"Primary Default": {
		component: PrimaryCard,
		variant: PrimaryCardVariants.default,
		showImage: true,
	},
};

const CTA_VARIANT_MAP = {
	Button: "button",
	Link: "link",
};

const CardConnector = ({ data }) => {
	const { locale } = usePageContext();
	const {
		component: Component,
		variant,
		showImage,
	} = CARD_VARIANT_MAP[data.variant] ?? CARD_VARIANT_MAP["Primary Default"];

	const ctaVariant = CTA_VARIANT_MAP[data.ctaVariant] ?? "link";

	const labels = {
		price: useTranslation({ searchKey: "Label_Price" }) || "Price",
		from: useTranslation({ searchKey: "from" }) || "from",
		to: useTranslation({ searchKey: "to" }) || "To",
	};

	const logo = data.logo?.[0]
		? cloudinaryGenURL({ cloudinaryImage: data.logo[0], maxWidth: 150 })
		: null;

	const themeConvert = theme => {
		if (theme == "Light") {
			return CardTheme.light;
		} else if (theme == "Dark") {
			return CardTheme.dark;
		} else {
			return null;
		}
	};

	let dataImage = data.image?.[0];
	let componentTheme = data.theme ? themeConvert(data.theme) : null;

	const adaptedImage = dataImage
		? {
				source: cloudinaryGenURL({
					cloudinaryImage: {
						public_id: dataImage?.public_id,
						baseUrl: dataImage.baseUrl,
						width: 800,
					},
					maxWidth: 800,
					baseUrl: dataImage.baseUrl,
					aspectRatio: 1.77777,
				}).data,
			}
		: null;

	let preferredImage = adaptedImage;

	if (data?.variant === "Hotel Default") {
		componentTheme = "light";
		dataImage = data.image;
		preferredImage = {
			alt: data.subheading,
			source: {
				aspectRatio: adaptedImage?.source?.aspectRatio,
				src: adaptedImage?.source?.src,
				srcSet: adaptedImage?.source?.srcSet,
				sizes: adaptedImage?.source?.sizes,
				sources: adaptedImage?.source?.source,
			},
		};
	}

	const backgroundTypeCovert = type => {
		if (type == "Transparent") {
			return CardBackgroundType.transparent;
		} else if (type == "Default") {
			return CardBackgroundType.solid;
		} else {
			return null;
		}
	};

	return (
		<Component
			key={data.id}
			variant={variant}
			theme={componentTheme}
			backgroundType={data.type ? backgroundTypeCovert(data.type) : null}
			header={data.heading}
			subheading={data.subheading}
			subheader={data.subheading}
			headerAs={data.headingAs}
			ctaVariant={ctaVariant}
			icon={data.icon}
			labels={{ ...data.labels, ...labels }}
			stars={data.stars}
			image={showImage && preferredImage}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(logo
				? {
						logo: {
							...logo,
							width: logo?.width,
							height: logo?.height,
						},
					}
				: {})}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(data.content ? { content: <RichTextConnector data={data.content} /> } : {})}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(data.link
				? {
						link: {
							icon:
								data.link.target === "_blank" || data.link.target === "EXTERNAL_MODAL"
									? "external-micro"
									: data.link.iconName,
							children: data.link.linkText,
							href: data.link.url,
							variant: data.link.variant,
							target: data.link.target,
						},
					}
				: {})}
			contentLimit={data.contentLimit}
			fromPrice={data.fromPrice}
			toPrice={data.toPrice}
			priceMessage={data.priceMessage}
			locale={locale}
		/>
	);
};

CardConnector.propTypes = {
	data: PropTypes.shape({
		id: PropTypes.string,
		heading: PropTypes.string,
		headingAs: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "p"]),
		subheading: PropTypes.string,
		variant: PropTypes.oneOf([
			"Hotel Default",
			"Primary Default",
			"Primary Even Split",
			"Primary Logo Offset",
			"Info Default",
			"Info Horizontal",
			"Info Horizontal No Icon",
			"Top Tile Default",
			"Top Tile Compact",
		]).isRequired,
		theme: PropTypes.string,
		type: PropTypes.string,
		image: PropTypes.arrayOf(
			PropTypes.shape({
				public_id: PropTypes.string,
				secure_url: PropTypes.string,
				baseUrl: PropTypes.string,
			})
		),
		logo: PropTypes.arrayOf(
			PropTypes.shape({
				public_id: PropTypes.string,
				secure_url: PropTypes.string,
			})
		),
		content: PropTypes.shape({
			raw: PropTypes.string,
		}),
		contentLimit: PropTypes.bool,
		icon: PropTypes.string,
		ctaVariant: PropTypes.oneOf(["Link", "Button"]),
		link: PropTypes.shape({
			variant: PropTypes.oneOf([
				"Primary",
				"Primary (Outline)",
				"Primary (Inverse)",
				"Primary (Outline and Inverse)",
				"Action",
				"Action (Outline)",
				"Action (Inverse)",
				"Action (Outline and Inverse)",
				"Utility",
			]).isRequired,
			linkText: PropTypes.string,
			url: PropTypes.string.isRequired,
			target: PropTypes.oneOf(["_blank", "_self", "_parent", "_top", "EXTERNAL_MODAL"]).isRequired,
			image: PropTypes.arrayOf(
				PropTypes.shape({
					public_id: PropTypes.string,
					secure_url: PropTypes.string,
				})
			),
			imageAltText: PropTypes.string,
			iconName: PropTypes.string,
			type: PropTypes.oneOf(["link", "button"]),
			align: PropTypes.oneOf(["left", "center", "right"]),
			modal: PropTypes.arrayOf(PropTypes.object),
			externalModalMessage: PropTypes.string,
		}),

		stars: PropTypes.number,
		hotelCode: PropTypes.string,
		labels: PropTypes.shape({
			allInc: PropTypes.string,
			from: PropTypes.string,
			to: PropTypes.string,
			price: PropTypes.string,
		}),
		fromPrice: PropTypes.number,
		toPrice: PropTypes.number,
		priceMessage: PropTypes.string,
		data: PropTypes.object,
	}).isRequired,
};

export { CardConnector };
